import React, { useMemo, useContext } from 'react'
import { useIntl } from 'react-intl'
import type { LoyaltyPointsEarningConfiguration } from 'components/Pages/CartReviewPage/CartReviewTile'
import CartReviewTile from 'components/Pages/CartReviewPage/CartReviewTile'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import { formatDynamicDateRange } from 'lib/datetime/dateUtils'
import CartReviewPriceStack from 'components/Pages/CartReviewPage/CartReviewPriceStack'
import CartReviewBanner from 'components/Pages/CartReviewPage/CartReviewBanner'
import { getBannerTypeVariant } from 'components/Pages/CartReviewPage/CartReviewUtils'
import useUserMultiCartBedbankHotelItemData from '../hooks/useUserMultiCartBedbankHotelItemData'
import offerPageURL from 'lib/offer/offerPageURL'
import CartReviewTileImageContent from 'components/Pages/CartReviewPage/CartReviewTileImageContent'
import { getUserCartItemDisplayTotals } from 'lib/userCart/userCartTotals'
import { isBedbankItem, isAccommodationItem } from 'lib/checkout/checkoutUtils'
import { MultiCartContext } from 'machines/cart/MultiCartMachineProvider'
import { useSelector } from '@xstate/react'
import { queryKeyCheckIn, queryKeyCheckOut, queryKeyAdults, queryKeyChildren } from 'constants/url'
import { encodeChildrenOccupancy } from 'lib/search/searchUtils'
import { generateLoyaltyPointsCalculatorBedbankOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import GeoContext from 'contexts/geoContext'
import MultiCartAccommodationTileComparisonCheckbox from './MultiCartAccommodationTileComparisonCheckbox'
import CondensedCartReviewTile from 'components/Pages/CartReviewPage/CondensedCartReviewTile'
import CondensedCartReviewTileContent from 'components/Pages/CartReviewPage/CondensedCartReviewTileContent'
import CartReviewTileContent from 'components/Pages/CartReviewPage/CartReviewTileContent'
import LoadingCartReviewTileSkeleton from 'components/Pages/CartReviewPage/LoadingCartReviewTileSkeleton'
import CartReviewTileActions from 'components/Pages/CartReviewPage/CartReviewTileActions'
import { MultiCartReviewMachineContext } from '../MultiCartReviewMachineContext'
import UndoDeletedItemsTile from '../UndoDeletedItemsTile'
import MultiCartParticipantLabel from './MultiCartParticipantLabel'
import SharedCartComments from '../Comments/SharedCartComments'
import { useAppSelector } from 'hooks/reduxHooks'
import { isLEAdmin } from 'selectors/accountSelectors'

interface Props {
  multiCartItem: App.MultiCartBedbankHotelItem
  className?: string
  hideTitleAndDates?: boolean
  hideAvailabilityDetails?: boolean
  disableOverlay?: boolean
  saveForLaterIfUnavailable?: boolean
  tileVariant?: 'default' | 'condensed'
}

function BedbankMultiCartReviewTile({
  multiCartItem,
  className,
  hideTitleAndDates = false,
  hideAvailabilityDetails = false,
  disableOverlay = false,
  saveForLaterIfUnavailable = true,
  tileVariant = 'default',
}: Props) {
  const intl = useIntl()
  const item = multiCartItem.item as App.Checkout.BedbankHotelItem
  const { currentRegionCode } = useContext(GeoContext)
  const data = useUserMultiCartBedbankHotelItemData(multiCartItem, saveForLaterIfUnavailable)

  const multiCartActorRef = MultiCartContext.useActorRef()
  const activeCartId = useSelector(multiCartActorRef, (snapshot) => snapshot.context.activeCartId)
  const cartItemsState = useSelector(multiCartActorRef, (snapshot) => snapshot.context.cartItems)
  const cartSummary = useSelector(multiCartActorRef, (snapshot) => snapshot.context.cartSummary)
  const multiCartReviewActorRef = MultiCartReviewMachineContext.useActorRef()
  const isAdmin = useAppSelector(isLEAdmin)
  const isSharedCart = cartSummary.status === 'loaded' && activeCartId && cartSummary.data[activeCartId]?.type !== 'default'

  const hotelItems = useMemo(() => {
    if (cartItemsState.status !== 'loaded' || !activeCartId) {
      return []
    }

    const multiCartItems = Object.values(cartItemsState.data[activeCartId] || {})

    return multiCartItems
      .filter(multiCartItem => multiCartItem.state === 'active') // Only active items (not deleted or save_for_later)
      .map(multiCartItem => multiCartItem.item)
      .filter(isAccommodationItem)
  }, [cartItemsState, activeCartId])

  const handleSaveForLater = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()

    multiCartReviewActorRef.send({
      type: 'update save items for later',
      cartId: activeCartId!,
      itemIds: [multiCartItem.id],
      saveForLater: !(multiCartItem.state === 'save_for_later'),
      skipNotification: true,
    })
  }
  const itemId = useMemo(() => multiCartItem.id, [multiCartItem.id])

  const handleDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()

    multiCartActorRef.send({
      type: 'delete items from given cart',
      cartId: activeCartId!,
      itemIds: [multiCartItem.id],
      currentStates: {
        [multiCartItem.id]: multiCartItem.state,
      },
    })
  }

  const formattedDateRange = useMemo(() => {
    if (!multiCartItem.checkIn || !multiCartItem.checkOut) return false
    return intl.formatMessage(
      {
        id: 'SIOXz4cyau6d_ggw2ITx',
        defaultMessage: '{dateRange} · {duration, plural, one {# night} other {# nights}}',
        description: 'Date range and stay duration for a bedbank cart review tile',
      },
      {
        dateRange: formatDynamicDateRange(multiCartItem.checkIn, multiCartItem.checkOut),
        duration: multiCartItem.duration,
      },
    )
  }, [intl, multiCartItem.checkIn, multiCartItem.checkOut, multiCartItem.duration])

  const formattedOccupancy = useMemo(() => {
    if (!multiCartItem.occupancy) return false
    return formatOccupantsShort(multiCartItem.occupancy)
  }, [multiCartItem.occupancy])

  const {
    publicTotal,
    memberTotal,
  } = useMemo(() => getUserCartItemDisplayTotals(data.itemTotals), [data?.itemTotals])

  const bannerTypeVariant = getBannerTypeVariant(data.isPast, data.isSoldOut, false, !!data.error)

  const offerLink = useMemo(() => {
    const params = new URLSearchParams()

    const itemGroup = hotelItems.filter(isBedbankItem).filter(i => i.offerId === multiCartItem.offerId && i.checkIn === multiCartItem.checkIn && i.checkOut === multiCartItem.checkOut)
    const rooms = itemGroup.map(i => i.occupancy)

    if (!data.isPast) {
      params.set(queryKeyCheckIn, multiCartItem.checkIn)
      params.set(queryKeyCheckOut, multiCartItem.checkOut)
    }

    params.set(queryKeyAdults, rooms.map(room => room.adults).join(','))
    params.set(queryKeyChildren, rooms.map(room => encodeChildrenOccupancy(room.childrenAge)).join(','))

    return offerPageURL(data.offer, params)
  }, [data.offer, hotelItems, multiCartItem, data.isPast])

  const loyaltyPointsEarningConfiguration = useMemo<LoyaltyPointsEarningConfiguration>(() => {
    if (!data.itemTotals || !data.offer || !data.rate) {
      return {
        calculationRequests: [
        ],
        calculationType: 'exact',
      }
    }
    const propertyFees = data.itemTotals.otherFees?.propertyFees ??
      data.itemTotals.propertiesFees?.reduce((acc, fee) => acc + fee.propertyFees, 0) ??
      0
    return {
      calculationRequests: [
        generateLoyaltyPointsCalculatorBedbankOptions(data.rate, {
          price: publicTotal,
          memberPrice: memberTotal,
          propertyFees,
          propertyId: data.offer.property.id,
          offerId: data.offer.id,
          propertyCountryCode: data.offer.property.address.countryCode,
          currentRegionCode,
        }),
      ],
      calculationType: 'exact',
    }
  }, [data.itemTotals, data.offer, data.rate, publicTotal, memberTotal, currentRegionCode])

  const priceTotals = useMemo(() => ({
    price: publicTotal,
    memberPrice: memberTotal,
    value: data.itemTotals?.value ?? 0,
  }), [data.itemTotals?.value, memberTotal, publicTotal])

  const filters = useMemo(() => ({
    checkIn: multiCartItem.checkIn,
    checkOut: multiCartItem.checkOut,
    rooms: [multiCartItem.occupancy],
    roomRateId: multiCartItem.roomRateId,
  }), [multiCartItem.checkIn, multiCartItem.checkOut, multiCartItem.occupancy, multiCartItem.roomRateId])

  const image = data.package?.images[0] || data.offer?.image

  if (data.loading) {
    return <LoadingCartReviewTileSkeleton />
  }

  if (multiCartItem.state === 'deleted') {
    return <UndoDeletedItemsTile
      title={data.offer?.property.name || data.offer?.name || ''}
      onUndoClick={() => {
        multiCartActorRef.send({
          itemIds: [itemId],
          type: 'undo delete',
        })
      }}
    />
  }

  if (tileVariant === 'condensed') {
    return (
      <CondensedCartReviewTile
        content={<CondensedCartReviewTileContent
          title={hideTitleAndDates ? undefined : data.offer?.property.name || data.offer?.name || ''}
          dates={hideTitleAndDates ? undefined : formattedDateRange}
          packageTitle={data.package?.name}
          occupancy={formattedOccupancy}
        />}
        imageElement={image ? <CartReviewTileImageContent image={image} /> : undefined}
        onDelete={handleDelete}
        banner={<CartReviewBanner
          variant={bannerTypeVariant}
        />}
      />
    )
  }
  const saveForLater = multiCartItem.state === 'save_for_later'

  // Only show comments footer for standalone tiles (not in multi-room groups) in shared carts
  const showComments = !hideTitleAndDates && activeCartId && isSharedCart
  const commentsFooter = showComments ? (
    <SharedCartComments
      multiCartId={activeCartId}
      itemIds={[multiCartItem.id]}
      isAdmin={isAdmin}
      noBorder
    />
  ) : undefined

  return (
    <CartReviewTile
      imageElement={image ? <CartReviewTileImageContent image={image} /> : undefined}
      to={offerLink}
      content={<CartReviewTileContent
        title={data.offer?.property.name || data.offer?.name || ''}
        dates={formattedDateRange}
        packageTitle={data.package?.name}
        occupancy={formattedOccupancy}
        loyaltyPointsEarningConfiguration={loyaltyPointsEarningConfiguration}
        comparisonCheckbox={<MultiCartAccommodationTileComparisonCheckbox
          offerType={data.offer?.type ?? 'bedbank_hotel'}
          cartItemId={item.itemId}
          cartId={activeCartId!}
          offerId={data.offer?.id || ''}
          roomId={data.package?.roomId}
          name={data.offer?.name || ''}
          filters={filters}
          priceTotals={priceTotals}
        />}
        label={<MultiCartParticipantLabel item={multiCartItem} />}
      />}
      price={<CartReviewPriceStack
        price={publicTotal}
        memberPrice={memberTotal}
        value={data.itemTotals?.value ?? 0}
        luxPlusOfferConfig={data.offer?.luxPlus}
      />}
      banner={<CartReviewBanner
        variant={bannerTypeVariant}
        to={offerLink}
      />}
      footer={commentsFooter}
      isBookable={data.isBookable}
      saveForLater={saveForLater}
      actions={<CartReviewTileActions
        isBookable={data.isBookable}
        saveForLater={saveForLater}
        onSaveForLater={handleSaveForLater}
        onDelete={handleDelete}
      />}
      className={className}
      hideAvailabilityDetails={hideAvailabilityDetails}
      disableOverlay={disableOverlay}
    />
  )
}

export default BedbankMultiCartReviewTile
